import { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/HighlightOff";
import WarningIcon from "@mui/icons-material/WarningAmberOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import {
  AgreementMappingFlags,
  IntegrationDataDisplayResponse,
  MappedPlanSlug,
} from "./IntegrationDataDisplay.types";
import {
  ContractIssue,
  getBlockingIssues,
  NormalizedContract,
  NormalizedIntegration,
  normalizeIntegrationData,
} from "./IntegrationDataDisplay.adapters";
import {
  buildMatchSummary,
  IntegrationPlanEntry,
  MatchSummary,
} from "./IntegrationDataDisplay.match";

interface IntegrationDataDisplayProps {
  data: IntegrationDataDisplayResponse;
  mappedPlanSlugs?: MappedPlanSlug[];
  erpSlugs?: string[];
  mappingFlags?: AgreementMappingFlags;
  onGoToMapping?: () => void;
}

type Tone = "success" | "warning" | "error" | "neutral";

const ISSUE_LABEL: Record<ContractIssue["kind"], string> = {
  vencido: "Vencido",
  cancelado: "Cancelado",
  suspenso: "Suspenso",
  inativo: "Inativo",
  atraso: "Em atraso",
};

/* ------------------------------------------------------------------ */
/* Primitivos visuais                                                  */
/* ------------------------------------------------------------------ */

/**
 * Rótulo de seção/coluna. A hierarquia vem de caixa alta + espaçamento +
 * cor secundária, não de negrito — assim o peso fica reservado ao conteúdo.
 */
const Label = ({
  children,
  sx,
}: {
  children: React.ReactNode;
  sx?: object;
}) => (
  <Typography
    variant="caption"
    sx={{
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      fontSize: "0.68rem",
      color: "text.secondary",
      ...sx,
    }}
  >
    {children}
  </Typography>
);

/** Destaque discreto dentro de frase — cor em vez de negrito */
const Highlight = ({ children }: { children: React.ReactNode }) => (
  <Box component="span" sx={{ color: "text.primary" }}>
    {children}
  </Box>
);

function useToneColor(tone: Tone) {
  const theme = useTheme();
  if (tone === "success") return theme.palette.success.main;
  if (tone === "warning") return theme.palette.warning.main;
  if (tone === "error") return theme.palette.error.main;
  return theme.palette.text.disabled;
}

/** Cartão com barra colorida à esquerda e título — base de todas as seções */
const Panel = ({
  title,
  action,
  tone = "neutral",
  dense,
  children,
}: {
  title?: React.ReactNode;
  action?: React.ReactNode;
  tone?: Tone;
  dense?: boolean;
  children: React.ReactNode;
}) => {
  const theme = useTheme();
  const color = useToneColor(tone);

  return (
    <Paper
      variant="outlined"
      sx={{
        overflow: "hidden",
        height: "100%",
        borderColor: tone === "neutral" ? theme.palette.divider : color,
      }}
    >
      {title && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{
            px: 2,
            py: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor:
              tone === "neutral"
                ? theme.palette.action.hover
                : `${color}14`,
          }}
        >
          <Label>{title}</Label>
          {action}
        </Stack>
      )}
      <Box sx={{ p: dense ? 0 : 2 }}>{children}</Box>
    </Paper>
  );
};

/** Contador grande usado no veredito */
const Metric = ({
  value,
  label,
  tone,
}: {
  value: number | string;
  label: string;
  tone: Tone;
}) => {
  const color = useToneColor(tone);

  return (
    <Box sx={{ minWidth: 88 }}>
      <Typography variant="h5" sx={{ fontWeight: 500, color, lineHeight: 1.1 }}>
        {value}
      </Typography>
      <Label>{label}</Label>
    </Box>
  );
};

/**
 * Mostra o valor exato que precisa ser cadastrado, com botão de copiar.
 * Monoespaçado porque espaço e acento importam no cadastro.
 */
const CopyValue = ({ value }: { value: string }) => {
  const theme = useTheme();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard bloqueado (contexto não seguro) — o texto segue selecionável
    }
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        pl: 1,
        pr: 0.25,
        py: 0.25,
        backgroundColor: theme.palette.action.hover,
        maxWidth: "100%",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontFamily: "monospace",
          wordBreak: "break-all",
          userSelect: "all",
        }}
      >
        {value}
      </Typography>
      <Tooltip title={copied ? "Copiado!" : "Copiar"}>
        <IconButton size="small" onClick={handleCopy} sx={{ p: 0.25 }}>
          {copied ? (
            <CheckIcon sx={{ fontSize: 14, color: "success.main" }} />
          ) : (
            <ContentCopyIcon sx={{ fontSize: 14 }} />
          )}
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

const StatusChip = ({ tone, label }: { tone: Tone; label: string }) => (
  <Chip
    size="small"
    label={label}
    color={tone === "neutral" ? "default" : tone}
    variant={tone === "success" ? "filled" : "outlined"}
    sx={{ height: 22 }}
  />
);

/* ------------------------------------------------------------------ */
/* Veredito — a resposta em uma olhada                                 */
/* ------------------------------------------------------------------ */

const VERDICT: Record<
  MatchSummary["status"],
  { tone: Tone; title: string; hint: string }
> = {
  ok: {
    tone: "success",
    title: "Contrato mapeado",
    hint: "Todos os planos da integração têm cadastro correspondente.",
  },
  partial: {
    tone: "warning",
    title: "Contrato parcialmente mapeado",
    hint: "Parte dos planos da integração não tem cadastro.",
  },
  none: {
    tone: "error",
    title: "Contrato não mapeado",
    hint: "Nenhum plano da integração tem cadastro no convênio.",
  },
  "no-plans": {
    tone: "warning",
    title: "Integração não retornou planos",
    hint: "A resposta veio sem planos para comparar.",
  },
  "no-mappings": {
    tone: "error",
    title: "Convênio sem planos cadastrados",
    hint: "Nada pode bater enquanto não houver cadastro.",
  },
};

const VerdictCard = ({
  summary,
  issues,
}: {
  summary: MatchSummary;
  issues: ContractIssue[];
}) => {
  const verdict = VERDICT[summary.status];

  // Contrato inválido explica a falha sozinho e tem precedência visual.
  const tone: Tone = issues.length ? "error" : verdict.tone;
  const color = useToneColor(tone);
  const Icon =
    tone === "success" ? CheckCircleIcon : tone === "error" ? CancelIcon : WarningIcon;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderColor: color,
        backgroundColor: `${color}0F`,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Icon sx={{ color, fontSize: 32 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
              {issues.length ? `Contrato ${ISSUE_LABEL[issues[0].kind].toLowerCase()}` : verdict.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {issues.length
                ? "O contrato do cliente não está válido — isso explica a falha, não o mapeamento."
                : verdict.hint}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={3} sx={{ pl: { xs: 5.5, sm: 0 } }}>
          <Metric
            value={summary.matched.length}
            label="mapeados"
            tone={summary.matched.length ? "success" : "neutral"}
          />
          <Metric
            value={summary.unmatched.length}
            label="sem mapeamento"
            tone={summary.unmatched.length ? "error" : "neutral"}
          />
          <Metric
            value={summary.totalMappings}
            label="cadastrados"
            tone="neutral"
          />
        </Stack>
      </Stack>

      {issues.length > 0 && (
        <Stack spacing={0.5} sx={{ mt: 1.5, pl: { xs: 0, sm: 5.5 } }}>
          {issues.map((issue, index) => (
            <Stack key={index} direction="row" spacing={1} alignItems="center">
              <EventBusyIcon sx={{ fontSize: 16, color }} />
              <Typography variant="body2">{issue.detail}</Typography>
            </Stack>
          ))}
        </Stack>
      )}

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1.5, pl: { xs: 0, sm: 5.5 } }}
      >
        Mapeamento por <Highlight>{summary.matchKeyLabel}</Highlight> ·
        comparação ignora maiúsculas/minúsculas ·{" "}
        {summary.entries.length} plano(s) na resposta
        {summary.erpLoaded
          ? ` · catálogo do ERP com ${summary.totalErpSlugs} planos`
          : ""}
      </Typography>
    </Paper>
  );
};

/* ------------------------------------------------------------------ */
/* Tabela: planos da integração                                        */
/* ------------------------------------------------------------------ */

const PlansTable = ({ entries }: { entries: IntegrationPlanEntry[] }) => {
  if (!entries.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Nenhum plano nesta lista.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer sx={{ maxHeight: 380 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>
              <Label>Plano na integração</Label>
            </TableCell>
            <TableCell sx={{ width: 90 }}>
              <Label>Id</Label>
            </TableCell>
            <TableCell>
              <Label>Resultado</Label>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map((entry) => {
            const matched = entry.matchKind === "exact";
            const partiuPlan = entry.match
              ? [entry.match.product_name, entry.match.plan_display_name]
                  .filter(Boolean)
                  .join(" / ")
              : undefined;

            return (
              <TableRow key={entry.name} hover>
                <TableCell sx={{ maxWidth: 260 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, wordBreak: "break-word" }}
                  >
                    {entry.name}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {[entry.code, entry.origins.join(", ")]
                      .filter(Boolean)
                      .join(" · ")}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {entry.id || "—"}
                  </Typography>
                </TableCell>

                <TableCell>
                  {matched ? (
                    <>
                      <StatusChip
                        tone="success"
                        label={`mapeado por ${
                          entry.matchedBy === "id" ? "id" : "nome"
                        }`}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.25 }}
                      >
                        → {partiuPlan || entry.match?.plan_slug_integration}
                      </Typography>
                    </>
                  ) : (
                    <Stack spacing={0.5} alignItems="flex-start">
                      <StatusChip tone="error" label="sem mapeamento" />
                      <Typography variant="caption" color="text.secondary">
                        Cadastre em{" "}
                        <Highlight>plan_slug_integration</Highlight>:
                      </Typography>
                      <CopyValue value={entry.name} />
                      {entry.id && (
                        <>
                          <Typography variant="caption" color="text.secondary">
                            ou em <Highlight>id_plan</Highlight>:
                          </Typography>
                          <CopyValue value={entry.id} />
                        </>
                      )}
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const IntegrationPlansPanel = ({ summary }: { summary: MatchSummary }) => {
  const [showMatched, setShowMatched] = useState(false);
  const entries = showMatched ? summary.matched : summary.unmatched;

  return (
    <Panel
      dense
      tone={summary.unmatched.length ? "error" : "success"}
      title={`Planos da integração (${summary.entries.length})`}
      action={
        <Stack direction="row" spacing={0.5}>
          <Chip
            size="small"
            label={`Não mapeados ${summary.unmatched.length}`}
            color={showMatched ? "default" : "error"}
            variant={showMatched ? "outlined" : "filled"}
            onClick={() => setShowMatched(false)}
            sx={{ height: 24, cursor: "pointer" }}
          />
          <Chip
            size="small"
            label={`Mapeados ${summary.matched.length}`}
            color={showMatched ? "success" : "default"}
            variant={showMatched ? "filled" : "outlined"}
            onClick={() => setShowMatched(true)}
            sx={{ height: 24, cursor: "pointer" }}
          />
        </Stack>
      }
    >
      <PlansTable entries={entries} />
    </Panel>
  );
};

/* ------------------------------------------------------------------ */
/* Painel: cadastrados no convênio                                     */
/* ------------------------------------------------------------------ */

const MappedList = ({
  items,
  used,
}: {
  items: MappedPlanSlug[];
  used: boolean;
}) => {
  const color = useToneColor(used ? "success" : "neutral");

  return (
    <Stack spacing={0.75}>
      {items.map((item, index) => {
        const detail = [item.product_name, item.plan_display_name]
          .filter(Boolean)
          .join(" / ");

        return (
          <Box
            key={`${item.plan_slug_integration}-${index}`}
            sx={{ borderLeft: `3px solid ${color}`, pl: 1.25 }}
          >
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="baseline"
              flexWrap="wrap"
            >
              <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                {item.plan_slug_integration}
              </Typography>
              {item.id_plan && (
                <Typography variant="caption" color="text.secondary">
                  id {item.id_plan}
                </Typography>
              )}
            </Stack>
            {detail && (
              <Typography variant="caption" color="text.secondary">
                {detail}
              </Typography>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

const MappedPanel = ({
  summary,
  onGoToMapping,
}: {
  summary: MatchSummary;
  onGoToMapping?: () => void;
}) => {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return summary.unusedMappings;
    return summary.unusedMappings.filter((item) =>
      [
        item.plan_slug_integration,
        item.id_plan,
        item.product_name,
        item.plan_display_name,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [summary.unusedMappings, search]);

  return (
    <Panel
      title={`Cadastrado no convênio (${summary.totalMappings})`}
      action={
        onGoToMapping && (
          <Button size="small" onClick={onGoToMapping}>
            Gerenciar
          </Button>
        )
      }
    >
      {!summary.totalMappings ? (
        <Typography variant="body2" color="text.secondary">
          Nenhum plano cadastrado neste convênio.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          <Box>
            <Label sx={{ display: "block", mb: 0.75 }}>
              Usados nesta consulta ({summary.usedMappings.length})
            </Label>
            {summary.usedMappings.length ? (
              <MappedList items={summary.usedMappings} used />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Nenhum cadastro bateu.
              </Typography>
            )}
          </Box>

          {summary.unusedMappings.length > 0 && (
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                backgroundColor: "transparent",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ px: 0, minHeight: 0 }}
              >
                <Label>Não usados ({summary.unusedMappings.length})</Label>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0, pt: 0 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Buscar por nome, id ou produto"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  sx={{ mb: 1 }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <Box sx={{ maxHeight: 260, overflowY: "auto", pr: 0.5 }}>
                  {filtered.length ? (
                    <MappedList items={filtered} used={false} />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Nada encontrado.
                    </Typography>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

          {summary.unmappedErpSlugs.length > 0 && (
            <>
              <Divider />
              <Typography variant="caption" color="text.secondary">
                {summary.unmappedErpSlugs.length} de {summary.totalErpSlugs}{" "}
                planos do catálogo do ERP ainda não têm cadastro.
              </Typography>
            </>
          )}
        </Stack>
      )}
    </Panel>
  );
};

/* ------------------------------------------------------------------ */
/* Painel: contratos do cliente                                        */
/* ------------------------------------------------------------------ */

const ContractRow = ({ contract }: { contract: NormalizedContract }) => {
  const hasIssue = contract.issues.length > 0;
  const color = useToneColor(hasIssue ? "error" : "success");

  return (
    <Box sx={{ borderLeft: `3px solid ${color}`, pl: 1.5, py: 0.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {contract.label}
        </Typography>
        {hasIssue ? (
          contract.issues.map((issue, index) => (
            <StatusChip key={index} tone="error" label={ISSUE_LABEL[issue.kind]} />
          ))
        ) : (
          <StatusChip tone="success" label="válido" />
        )}
      </Stack>

      {contract.dates.length > 0 && (
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.25 }}>
          {contract.dates.map((date) => (
            <Typography
              key={date.label}
              variant="caption"
              color={date.overdue ? "error.main" : "text.secondary"}
            >
              {date.label}{" "}
              <Box component="span" sx={{ color: "text.primary" }}>
                {date.value}
              </Box>
            </Typography>
          ))}
        </Stack>
      )}

      {contract.plans.length > 0 && (
        <Typography variant="caption" color="text.disabled" component="div">
          {contract.plans.map((plan) => plan.name).join(" · ")}
        </Typography>
      )}
    </Box>
  );
};

const ClientPanel = ({
  integration,
}: {
  integration: NormalizedIntegration;
}) => {
  const { contracts, clients } = integration;
  if (!contracts.length && !clients.length) return null;

  return (
    <Panel title={`Contratos e cliente (${contracts.length})`}>
      <Stack spacing={1.5}>
        {contracts.map((contract) => (
          <ContractRow key={contract.key} contract={contract} />
        ))}

        {clients.length > 0 && contracts.length > 0 && <Divider />}

        {clients.map((client, index) => (
          <Stack
            key={index}
            direction="row"
            spacing={3}
            flexWrap="wrap"
            useFlexGap
          >
            {client.name && (
              <Box>
                <Label sx={{ display: "block" }}>Nome</Label>
                <Typography variant="body2">{client.name}</Typography>
              </Box>
            )}
            {client.document && (
              <Box>
                <Label sx={{ display: "block" }}>Documento</Label>
                <Typography variant="body2">{client.document}</Typography>
              </Box>
            )}
            {client.contact && (
              <Box>
                <Label sx={{ display: "block" }}>Contato</Label>
                <Typography variant="body2">{client.contact}</Typography>
              </Box>
            )}
            {client.address && (
              <Box sx={{ maxWidth: 420 }}>
                <Label sx={{ display: "block" }}>Endereço</Label>
                <Typography variant="body2">{client.address}</Typography>
              </Box>
            )}
          </Stack>
        ))}
      </Stack>
    </Panel>
  );
};

/* ------------------------------------------------------------------ */

export function IntegrationDataDisplay({
  data,
  mappedPlanSlugs,
  erpSlugs,
  mappingFlags,
  onGoToMapping,
}: IntegrationDataDisplayProps) {
  const integrationData = data?.integration_data;

  const integration = useMemo(
    () => normalizeIntegrationData(integrationData, mappingFlags ?? {}),
    [integrationData, mappingFlags]
  );

  const summary = useMemo(
    () =>
      buildMatchSummary(
        integration,
        mappedPlanSlugs ?? [],
        erpSlugs,
        mappingFlags ?? {}
      ),
    [integration, mappedPlanSlugs, erpSlugs, mappingFlags]
  );

  const blockingIssues = useMemo(
    () => getBlockingIssues(integration),
    [integration]
  );

  if (!data) return null;

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <VerdictCard summary={summary} issues={blockingIssues} />

      <IntegrationPlansPanel summary={summary} />

      <MappedPanel summary={summary} onGoToMapping={onGoToMapping} />

      <ClientPanel integration={integration} />
    </Stack>
  );
}
